/**
 * World Conquest screen - producer-based infiltration system
 * Agents generate infiltration power that fills conquest gauge in real-time
 * Phase 2: City backgrounds + agent bubble visuals
 */

import { COUNTRIES, getFacilityCost, isCountryUnlocked, getConqueredCount, getInfiltrationPower } from '../game/index.js';
import { PRODUCERS } from '../game/data/producers.js';
import type { FacilityDef } from '../game/index.js';
import { formatNumber, formatCps } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { getState, getViewState, setViewState, onRender, dispatch, setInnerHTML, showAdRewardPopup } from './renderer.js';

/** Background image map per country */
const COUNTRY_BG: Record<string, string> = {
  egypt: 'images/conquest-bg-egypt.jpg',
  brazil: 'images/conquest-bg-brazil.jpg',
  india: 'images/conquest-bg-india.jpg',
  australia: 'images/conquest-bg-australia.jpg',
  japan: 'images/conquest-bg-japan.jpg',
  korea: 'images/conquest-bg-korea.jpg',
  uk: 'images/conquest-bg-uk.jpg',
  russia: 'images/conquest-bg-russia.jpg',
  china: 'images/conquest-bg-china.jpg',
  usa: 'images/conquest-bg-usa.jpg',
};

/** Facility nameKey → image file mapping */
const FACILITY_IMG: Record<string, string> = {
  fac_pyramid_vault: 'images/fac-pyramid-vault.png',
  fac_nile_network: 'images/fac-nile-network.png',
  fac_ancient_lab: 'images/fac-ancient-lab.png',
  fac_amazon_hideout: 'images/fac-amazon-hideout.png',
  fac_samba_broadcast: 'images/fac-samba-broadcast.png',
  fac_jungle_lab: 'images/fac-jungle-lab.png',
  fac_naga_temple: 'images/fac-naga-temple.png',
  fac_bollywood_studio: 'images/fac-bollywood-studio.png',
  fac_caste_network: 'images/fac-caste-network.png',
  fac_desert_bunker: 'images/fac-desert-bunker.png',
  fac_ayers_signal: 'images/fac-ayers-signal.png',
  fac_outback_base: 'images/fac-outback-base.png',
  fac_akiba_ai: 'images/fac-akiba-ai.png',
  fac_ninja_intel: 'images/fac-ninja-intel.png',
  fac_tokyo_tower: 'images/fac-tokyo-tower.png',
  fac_gangnam_hq: 'images/fac-gangnam-hq.png',
  fac_cyber_command: 'images/fac-cyber-command.png',
  fac_kpop_brainwash: 'images/fac-kpop-brainwash.png',
  fac_royal_infiltrate: 'images/fac-royal-infiltrate.png',
  fac_london_tower: 'images/fac-london-tower.png',
  fac_mi6_double: 'images/fac-mi6-double.png',
  fac_siberia_base: 'images/fac-siberia-base.png',
  fac_kgb_remnant: 'images/fac-kgb-remnant.png',
  fac_nuke_bunker: 'images/fac-nuke-bunker.png',
  fac_great_wall: 'images/fac-great-wall.png',
  fac_shenzhen_factory: 'images/fac-shenzhen-factory.png',
  fac_panda_spy: 'images/fac-panda-spy.png',
  fac_area51: 'images/fac-area51.png',
  fac_pentagon: 'images/fac-pentagon.png',
  fac_wall_street: 'images/fac-wall-street.png',
};

/** Custom background-position for images with off-center landmarks */
const COUNTRY_BG_POS: Record<string, string> = {
  australia: 'center 75%',
  japan: 'center 70%',
  russia: 'center 65%',
};

const CQ_TUTORIAL_KEY = 'cq_tutorial_seen';
let tutorialShown = false;

export function initDominationScreen() {
  onRender(renderConquestScreen, { alwaysTabs: ['domination'] });
}

function showConquestTutorial() {
  if (tutorialShown || localStorage.getItem(CQ_TUTORIAL_KEY)) return;
  tutorialShown = true;
  localStorage.setItem(CQ_TUTORIAL_KEY, '1');

  showAdRewardPopup(
    t('cq_tutorial_title'),
    t('cq_tutorial_text'),
    () => { /* dismiss */ },
    t('confirm'),
    '',
  );
  // Hide cancel button since this is informational
  setTimeout(() => {
    const cancelBtn = document.getElementById('ad-modal-cancel');
    if (cancelBtn) cancelBtn.style.display = 'none';
    // Change confirm button to not have ad icon
    const confirmBtn = document.getElementById('ad-modal-confirm');
    if (confirmBtn) confirmBtn.textContent = t('confirm');
  }, 0);
}

function renderConquestScreen() {
  if (getViewState().currentTab !== 'domination') return;

  const container = document.getElementById('screen-content');
  if (!container) return;

  const state = getState();
  const selectedId = getViewState().conquestSelectedCountry;

  // Show tutorial on first visit
  showConquestTutorial();

  // If a country is selected, show detail view
  if (selectedId) {
    listStructureKey = ''; // reset list cache when entering detail
    renderCountryDetail(container, selectedId);
    return;
  }

  // Ending screen
  if (state.conquest.endingUnlocked) {
    listStructureKey = '';
    detailStructureKey = '';
    renderEndingScreen(container);
    return;
  }

  // Main country list
  detailStructureKey = ''; // reset detail cache when returning to list
  renderCountryList(container);
}

/** Stable structure key for country list to avoid blink on tick updates */
let listStructureKey = '';

function renderCountryList(container: HTMLElement) {
  const state = getState();
  const conquered = getConqueredCount(state);
  const infPower = getInfiltrationPower(state);
  const activeId = state.conquest.activeCountry;

  // Build a structure key from things that change the DOM layout (not values)
  const structKey = COUNTRIES.map(c => {
    const unlocked = isCountryUnlocked(c.id, state);
    const cs = state.conquest.countries[c.id];
    const isConquered = cs?.conquered ?? false;
    const isActive = activeId === c.id;
    return `${c.id}:${unlocked ? 'u' : 'l'}:${isConquered ? 'c' : isActive ? 'a' : 'p'}`;
  }).join('|') + `|conquered:${conquered}`;

  // Only rebuild full HTML when structure changes
  if (structKey !== listStructureKey) {
    listStructureKey = structKey;

    let html = `<div class="conquest-screen">`;
    html += `
      <div class="conquest-header">
        <div class="conquest-title">${t('cq_title')}</div>
        <div class="conquest-progress-text">${t('cq_progress')}: <span id="cq-list-conquered">${conquered}</span> / ${COUNTRIES.length}</div>
        <div class="conquest-progress-bar">
          <div class="conquest-progress-fill" id="cq-list-progress-fill" style="width: ${(conquered / COUNTRIES.length) * 100}%"></div>
        </div>
        <div class="conquest-power-display">
          \u{1F575} ${t('cq_infiltration_power')}: <span class="conquest-power-value" id="cq-list-power">${formatCps(infPower)}</span>
        </div>
      </div>
    `;

    html += `<div class="conquest-countries">`;
    for (const country of COUNTRIES) {
      const unlocked = isCountryUnlocked(country.id, state);
      const cs = state.conquest.countries[country.id];
      const isConquered = cs?.conquered ?? false;
      const invested = cs?.invested ?? 0;
      const progress = Math.min(100, (invested / country.conquestCost) * 100);
      const isActive = activeId === country.id;

      let statusClass = 'locked';
      let statusText = t('cq_locked');
      if (isConquered) {
        statusClass = 'conquered';
        statusText = t('cq_conquered');
      } else if (unlocked) {
        if (isActive) {
          statusClass = 'infiltrating';
          statusText = t('cq_active_conquest');
        } else if (invested > 0) {
          statusClass = 'in-progress';
          statusText = `${progress.toFixed(1)}%`;
        } else {
          statusClass = 'available';
          statusText = t('cq_available');
        }
      }

      let etaText = '';
      if (isActive && !isConquered && infPower > 0) {
        const remaining = country.conquestCost - invested;
        const etaSec = remaining / infPower;
        if (etaSec < 60) etaText = `~${Math.ceil(etaSec)}s`;
        else if (etaSec < 3600) etaText = `~${Math.ceil(etaSec / 60)}m`;
        else etaText = `~${(etaSec / 3600).toFixed(1)}h`;
      }

      html += `
        <div class="conquest-card ${statusClass} ${isActive ? 'active-target' : ''}" data-country="${country.id}">
          <div class="conquest-card-flag">${country.flag}</div>
          <div class="conquest-card-info">
            <div class="conquest-card-name">${t(country.nameKey)}</div>
            ${!isConquered && unlocked ? `
              <div class="conquest-card-bar">
                <div class="conquest-card-bar-fill ${isActive ? 'animating' : ''}" id="cq-bar-${country.id}" style="width: ${progress}%"></div>
              </div>
              <div class="conquest-card-cost" id="cq-cost-${country.id}">${progress.toFixed(1)}% ${etaText ? `(${etaText})` : ''}</div>
            ` : ''}
            ${isConquered ? `
              <div class="conquest-card-facilities-mini">
                ${country.facilities.map((_f, i) => {
                  const lv = cs!.facilities[i] || 0;
                  return `<span class="conquest-fac-badge">${lv > 0 ? `Lv.${lv}` : '-'}</span>`;
                }).join('')}
              </div>
            ` : ''}
          </div>
          <div class="conquest-card-status ${statusClass}" id="cq-status-${country.id}">${statusText}</div>
        </div>
      `;
    }
    html += `</div></div>`;

    container.innerHTML = html;

    // Bind click events
    container.querySelectorAll('.conquest-card[data-country]').forEach(el => {
      const countryId = (el as HTMLElement).dataset.country!;
      const unlocked = isCountryUnlocked(countryId, state);
      if (!unlocked) return;
      el.addEventListener('click', () => {
        setViewState({ conquestSelectedCountry: countryId });
      });
    });
  } else {
    // Structure unchanged — patch dynamic values only (no innerHTML rebuild)
    const powerEl = document.getElementById('cq-list-power');
    if (powerEl) powerEl.textContent = formatCps(infPower);

    for (const country of COUNTRIES) {
      const cs = state.conquest.countries[country.id];
      const isConquered = cs?.conquered ?? false;
      if (isConquered) continue;

      const unlocked = isCountryUnlocked(country.id, state);
      if (!unlocked) continue;

      const invested = cs?.invested ?? 0;
      const progress = Math.min(100, (invested / country.conquestCost) * 100);
      const isActive = activeId === country.id;

      const barEl = document.getElementById(`cq-bar-${country.id}`) as HTMLElement | null;
      if (barEl) barEl.style.width = `${progress}%`;

      let etaText = '';
      if (isActive && infPower > 0) {
        const remaining = country.conquestCost - invested;
        const etaSec = remaining / infPower;
        if (etaSec < 60) etaText = `~${Math.ceil(etaSec)}s`;
        else if (etaSec < 3600) etaText = `~${Math.ceil(etaSec / 60)}m`;
        else etaText = `~${(etaSec / 3600).toFixed(1)}h`;
      }

      const costEl = document.getElementById(`cq-cost-${country.id}`);
      if (costEl) costEl.textContent = `${progress.toFixed(1)}% ${etaText ? `(${etaText})` : ''}`;

      // Update status text for in-progress (non-active) countries
      if (!isActive && invested > 0) {
        const statusEl = document.getElementById(`cq-status-${country.id}`);
        if (statusEl) statusEl.textContent = `${progress.toFixed(1)}%`;
      }
    }
  }
}

/** Stable structure key for detail view to avoid blink on tick updates */
let detailStructureKey = '';

function renderCountryDetail(container: HTMLElement, countryId: string) {
  const state = getState();
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return;

  const cs = state.conquest.countries[countryId];
  const isConquered = cs?.conquered ?? false;
  const invested = cs?.invested ?? 0;
  const progress = Math.min(100, (invested / country.conquestCost) * 100);
  const infPower = getInfiltrationPower(state);
  const isActive = state.conquest.activeCountry === countryId;
  const bgUrl = COUNTRY_BG[countryId] || '';

  // Collect deployed agents (producers with count > 0)
  const deployedAgents: Array<{ id: string; count: number; img: string }> = [];
  for (const prod of PRODUCERS) {
    const owned = state.producers[prod.id]?.count ?? 0;
    if (owned > 0) {
      deployedAgents.push({ id: prod.id, count: owned, img: `images/prod-${prod.id}.png` });
    }
  }

  // Structure key: country, conquered status, active status, agent list (ids only), facility levels
  const agentIds = deployedAgents.map(a => a.id).join(',');
  const facLevels = isConquered && cs ? cs.facilities.join(',') : '';
  const structKey = `detail:${countryId}:${isConquered}:${isActive}:${agentIds}:${facLevels}`;

  if (structKey !== detailStructureKey) {
    detailStructureKey = structKey;

    let html = `<div class="conquest-screen">`;

    // City background hero section
    html += `
      <div class="conquest-city-hero" style="background-image: url('${bgUrl}')${COUNTRY_BG_POS[countryId] ? `;background-position:${COUNTRY_BG_POS[countryId]}` : ''}">
        <div class="conquest-city-overlay">
          <button class="conquest-back-btn conquest-back-float" id="cq-back">\u{2190}</button>
          <div class="conquest-city-title">${country.flag} ${t(country.nameKey)}</div>
          <div class="conquest-city-desc">${t(country.descKey)}</div>
        </div>
        ${isConquered && cs ? (() => {
          const builtFacs = country.facilities
            .map((f, i) => ({ def: f, level: cs.facilities[i] }))
            .filter(f => f.level > 0);
          return builtFacs.length > 0 ? `
            <div class="conquest-agents-area">
              ${builtFacs.map((f, i) => `
                <div class="conquest-agent-bubble conquest-fac-bubble" style="animation-delay: ${i * 0.4}s">
                  <img src="${FACILITY_IMG[f.def.nameKey] || ''}" alt="${f.def.nameKey}" class="conquest-agent-img">
                  <span class="conquest-agent-count">Lv.${f.level}</span>
                </div>
              `).join('')}
            </div>
          ` : '';
        })() : deployedAgents.length > 0 ? `
          <div class="conquest-agents-area">
            ${deployedAgents.slice(0, 6).map((a, i) => `
              <div class="conquest-agent-bubble" style="animation-delay: ${i * 0.3}s">
                <img src="${a.img}" alt="${a.id}" class="conquest-agent-img">
                <span class="conquest-agent-count" id="cq-agent-${a.id}">x${a.count > 999 ? formatNumber(a.count) : a.count}</span>
              </div>
            `).join('')}
            ${deployedAgents.length > 6 ? `<div class="conquest-agent-more">+${deployedAgents.length - 6}</div>` : ''}
          </div>
        ` : ''}
      </div>
    `;

    // Infiltration section (if not yet conquered)
    if (!isConquered) {
      let etaText = '-';
      if (infPower > 0) {
        const remaining = country.conquestCost - invested;
        const etaSec = remaining / infPower;
        if (etaSec < 60) etaText = `${Math.ceil(etaSec)}s`;
        else if (etaSec < 3600) etaText = `${Math.ceil(etaSec / 60)}m`;
        else etaText = `${(etaSec / 3600).toFixed(1)}h`;
      }

      html += `
        <div class="conquest-infiltration-section">
          <div class="conquest-infiltration-title">\u{1F575} ${t('cq_infiltration_progress')}</div>
          <div class="conquest-infiltration-bar">
            <div class="conquest-infiltration-bar-fill ${isActive ? 'animating' : ''}" id="cq-detail-bar-fill" style="width: ${progress}%"></div>
            <div class="conquest-infiltration-bar-text" id="cq-detail-bar-text">${progress.toFixed(1)}%</div>
          </div>
          <div class="conquest-infiltration-stats">
            <div class="conquest-stat-row">
              <span class="conquest-stat-label">${t('cq_infiltration_power')}</span>
              <span class="conquest-stat-value" id="cq-detail-power">${formatCps(infPower)}</span>
            </div>
            <div class="conquest-stat-row">
              <span class="conquest-stat-label">${t('cq_time_remaining')}</span>
              <span class="conquest-stat-value" id="cq-detail-eta">${isActive ? etaText : '-'}</span>
            </div>
            <div class="conquest-stat-row">
              <span class="conquest-stat-label">${t('cq_progress')}</span>
              <span class="conquest-stat-value" id="cq-detail-invested">${formatNumber(invested)} / ${formatNumber(country.conquestCost)}</span>
            </div>
          </div>
          ${isActive
            ? `<button class="conquest-action-btn stop" id="cq-stop">\u{23F8} ${t('cq_stop_infiltration')}</button>`
            : `<button class="conquest-action-btn start ${infPower > 0 ? '' : 'disabled'}" id="cq-start" ${infPower > 0 ? '' : 'disabled'}>\u{25B6} ${t('cq_start_infiltration')}</button>`
          }
          ${!isActive && infPower === 0 ? `<div class="conquest-no-power-hint">${t('cq_no_power_hint')}</div>` : ''}
        </div>
      `;
    } else {
      html += `<div class="conquest-conquered-badge">\u{2705} ${t('cq_conquered')}</div>`;
    }

    // Facilities (only show if conquered)
    if (isConquered) {
      html += `<div class="conquest-facilities-title">${t('cq_facilities')}</div>`;
      html += `<div class="conquest-facilities">`;
      for (let i = 0; i < country.facilities.length; i++) {
        const fac = country.facilities[i];
        const lv = cs!.facilities[i] || 0;
        const cost = getFacilityCost(fac.baseCost, lv);
        const canAfford = state.ip >= cost;
        const effectLabel = getEffectLabel(fac.effectType);

        html += `
          <div class="conquest-facility-card">
            <div class="conquest-fac-name">${t(fac.nameKey)}</div>
            <div class="conquest-fac-desc">${t(fac.descKey)}</div>
            <div class="conquest-fac-level">Lv. ${lv}</div>
            <div class="conquest-fac-effect">${effectLabel} +${fac.effectPerLevel * lv}%</div>
            <div class="conquest-fac-next">
              ${t('cq_next_level')}: ${effectLabel} +${fac.effectPerLevel * (lv + 1)}%
            </div>
            <button class="conquest-fac-btn ${canAfford ? '' : 'disabled'}" data-fac-idx="${i}" ${canAfford ? '' : 'disabled'}>
              ${lv === 0 ? t('cq_build') : t('cq_upgrade')} - ${formatNumber(cost)} \u{1F6F8}
            </button>
          </div>
        `;
      }
      html += `</div>`;
    }

    html += `</div>`;

    container.innerHTML = html;

    // Back
    document.getElementById('cq-back')?.addEventListener('click', () => {
      setViewState({ conquestSelectedCountry: null });
    });

    // Start infiltration
    document.getElementById('cq-start')?.addEventListener('click', () => {
      dispatch({ type: 'SET_ACTIVE_COUNTRY', payload: { countryId } });
    });

    // Stop infiltration
    document.getElementById('cq-stop')?.addEventListener('click', () => {
      dispatch({ type: 'SET_ACTIVE_COUNTRY', payload: { countryId: null } });
    });

    // Facility buttons
    container.querySelectorAll('.conquest-fac-btn:not(.disabled)').forEach(el => {
      el.addEventListener('click', () => {
        const facIdx = parseInt((el as HTMLElement).dataset.facIdx!, 10);
        const lv = cs?.facilities[facIdx] ?? 0;
        if (lv === 0) {
          dispatch({ type: 'BUILD_FACILITY', payload: { countryId, facilityIdx: facIdx } });
        } else {
          dispatch({ type: 'UPGRADE_FACILITY', payload: { countryId, facilityIdx: facIdx } });
        }
      });
    });
  } else {
    // Structure unchanged — patch dynamic values only
    if (!isConquered) {
      const barFill = document.getElementById('cq-detail-bar-fill') as HTMLElement | null;
      if (barFill) barFill.style.width = `${progress}%`;

      const barText = document.getElementById('cq-detail-bar-text');
      if (barText) barText.textContent = `${progress.toFixed(1)}%`;

      const powerEl = document.getElementById('cq-detail-power');
      if (powerEl) powerEl.textContent = formatCps(infPower);

      let etaText = '-';
      if (isActive && infPower > 0) {
        const remaining = country.conquestCost - invested;
        const etaSec = remaining / infPower;
        if (etaSec < 60) etaText = `${Math.ceil(etaSec)}s`;
        else if (etaSec < 3600) etaText = `${Math.ceil(etaSec / 60)}m`;
        else etaText = `${(etaSec / 3600).toFixed(1)}h`;
      }
      const etaEl = document.getElementById('cq-detail-eta');
      if (etaEl) etaEl.textContent = isActive ? etaText : '-';

      const investedEl = document.getElementById('cq-detail-invested');
      if (investedEl) investedEl.textContent = `${formatNumber(invested)} / ${formatNumber(country.conquestCost)}`;
    }

    // Update agent counts
    for (const a of deployedAgents) {
      const el = document.getElementById(`cq-agent-${a.id}`);
      if (el) el.textContent = `x${a.count > 999 ? formatNumber(a.count) : a.count}`;
    }
  }
}

function renderEndingScreen(container: HTMLElement) {
  const state = getState();
  const playTimeHours = Math.floor(state.totalPlayTimeMs / 3_600_000);
  const playTimeMins = Math.floor((state.totalPlayTimeMs % 3_600_000) / 60_000);

  let html = `<div class="conquest-screen">`;
  html += `
    <div class="conquest-ending">
      <div class="conquest-ending-icon">&#127758;</div>
      <div class="conquest-ending-title">${t('cq_ending_title')}</div>
      <div class="conquest-ending-text">${t('cq_ending_text')}</div>
      <div class="conquest-ending-stats">
        <div>${t('cq_ending_time')}: ${playTimeHours}h ${playTimeMins}m</div>
        <div>${t('cq_ending_prestiges')}: ${state.prestigeCount}</div>
        <div>${t('cq_ending_total_ip')}: ${formatNumber(state.totalIpEarned)}</div>
        <div>${t('cq_ending_taps')}: ${formatNumber(state.tapCount)}</div>
      </div>
      <button class="conquest-ending-btn" id="cq-continue">${t('cq_ending_continue')}</button>
    </div>
  `;
  html += `</div>`;

  if (setInnerHTML(container, html)) {
    document.getElementById('cq-continue')?.addEventListener('click', () => {
      setViewState({ conquestSelectedCountry: null });
    });
  }
}

function getEffectLabel(type: FacilityDef['effectType']): string {
  switch (type) {
    case 'cps_pct': return '🛸/s';
    case 'tap_pct': return 'TAP';
    case 'offline_pct': return 'OFFLINE';
    case 'gc_pct': return '\u{1F48E}';
    case 'golden_pct': return 'GOLDEN';
    case 'producer_discount': return 'DISCOUNT';
  }
}
